import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-[2px] border border-transparent px-2 py-0.5 font-[family-name:var(--font-primary)] text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
        outline:
          "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        /** Strategies table — active composed Pine */
        "pine-compose-active":
          "border-0 bg-transparent text-white bg-[image:var(--gradient-primary)] shadow-none",
        "pine-compose-stale":
          "border border-solid border-amber-500/50 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-compose-none":
          "border border-solid border-gray-500/50 bg-transparent text-gray-600 dark:text-gray-400",
        "pine-master-script":
          "border-[color:var(--brand-tropical-surf)]/50 bg-transparent text-[color:var(--brand-tropical-surf)]",
        "pine-signals-only":
          "border-amber-500/40 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-master-none":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-script-kind":
          "border-violet-500/40 bg-transparent text-violet-700 dark:text-violet-200",
        "pine-script-none":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-parse-success":
          "border border-solid border-[color:var(--tessera-pnl-positive-border)] bg-transparent text-[color:var(--tessera-pnl-positive)]",
        "pine-parse-partial":
          "border-amber-500/40 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-parse-failed":
          "border-red-500/40 bg-transparent text-red-700 dark:text-red-200",
        "pine-parse-none":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-inject-ready":
          "border border-solid border-[color:var(--tessera-pnl-positive-border)] bg-transparent text-[color:var(--tessera-pnl-positive)]",
        "pine-inject-review":
          "border-amber-500/40 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-inject-unsupported":
          "border-red-500/40 bg-transparent text-red-700 dark:text-red-200",
        "pine-inject-na":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-inject-none":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-exit-ready":
          "border border-solid border-[color:var(--tessera-pnl-positive-border)] bg-transparent text-[color:var(--tessera-pnl-positive)]",
        "pine-exit-partial":
          "border-amber-500/40 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-exit-unsupported":
          "border-red-500/40 bg-transparent text-red-700 dark:text-red-200",
        "pine-exit-na":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-exit-none":
          "border-gray-400/50 bg-transparent text-gray-500 dark:text-gray-400",
        "pine-meta":
          "border-gray-500/50 bg-transparent text-gray-600 dark:text-gray-400",
        /** Strategy detail — Pine status panel (canonical labels) */
        "pine-status-active-compose":
          "border-0 bg-transparent text-white bg-[image:var(--gradient-primary)] shadow-none",
        "pine-status-signals-only":
          "border border-solid border-amber-500/50 bg-transparent text-amber-800 dark:text-amber-200",
        "pine-status-no-script":
          "border border-solid border-gray-500/50 bg-transparent text-gray-600 dark:text-gray-400",
        "pine-status-not-parsed":
          "border border-solid border-slate-500/45 bg-transparent text-slate-600 dark:text-slate-300",
        "pine-status-ready":
          "border border-solid border-[color:var(--tessera-pnl-positive-border)] bg-transparent text-[color:var(--tessera-pnl-positive)]",
        "pine-status-ready-for-merge":
          "border border-solid border-[color:var(--brand-tropical-surf)]/50 bg-transparent text-[color:var(--brand-tropical-surf)]",
        "pine-status-inject-unknown":
          "border border-solid border-gray-500/45 bg-transparent text-gray-600 dark:text-gray-400",
        "pine-status-exit-hooks-unknown":
          "border border-solid border-zinc-500/45 bg-transparent text-zinc-600 dark:text-zinc-300",
        "pine-status-indicator-overlay":
          "border border-solid border-[color:var(--brand-tropical-surf)]/50 bg-transparent text-[color:var(--brand-tropical-surf)]",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
